import JSZip from 'jszip'
import {
  buildManifestJson,
  getCrashEntryStem,
  prepareCrashPayloadForZipExport,
  resolveSingleSourceOrganization,
  sanitizeCrashEntryFilename,
} from './crash-package-export'
import { CrashPackageZipError } from './crash-package-zip-error'
import { generateClientUuid } from './uuid'

/**
 * Costruisce un ZIP importabile (manifest + crashes/ + allegati) da righe locali.
 *
 * @param {Object} params
 * @param {Array<{ clientUuid: string, payload: object }>} params.rows
 * @param {Record<string, unknown> | null} params.formModel
 * @param {string | number} params.createdByUserId
 * @returns {Promise<{ blob: Blob, manifest: object, packageId: string, clientUuids: string[] }>}
 */
export async function createCrashPackageZip({
  rows,
  formModel,
  createdByUserId,
}) {
  assertExportableRows(rows, createdByUserId)
  const organizationId = resolveOrganizationId(rows)
  const packageId = generateClientUuid()
  const manifest = buildManifestJson({
    sourceOrganizationId: organizationId,
    createdByUserId: Number(createdByUserId),
    packageId,
    exportedAt: new Date().toISOString(),
  })
  const zip = new JSZip()
  zip.file('manifest.json', JSON.stringify(manifest, null, 2))
  const clientUuids = await appendCrashesToZip(zip, rows, formModel)
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
  return { blob, manifest, packageId, clientUuids }
}

/**
 * @param {Array<{ clientUuid: string, payload: object }> | null | undefined} rows
 * @param {string | number | null | undefined} createdByUserId
 */
function assertExportableRows(rows, createdByUserId) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new CrashPackageZipError('EMPTY', 'No crashes to export')
  }
  if (createdByUserId == null || createdByUserId === '') {
    throw new CrashPackageZipError('USER_MISSING', 'User id is missing')
  }
}

/**
 * @param {Array<{ payload: object }>} rows
 * @returns {number}
 */
function resolveOrganizationId(rows) {
  const orgResult = resolveSingleSourceOrganization(rows)
  if (!orgResult.ok) {
    throw new CrashPackageZipError(orgResult.code, orgResult.code)
  }
  return orgResult.organizationId
}

/**
 * @param {import('jszip')} zip
 * @param {Array<{ clientUuid: string, payload: object }>} rows
 * @param {Record<string, unknown> | null} formModel
 * @returns {Promise<string[]>}
 */
async function appendCrashesToZip(zip, rows, formModel) {
  const crashesFolder = zip.folder('crashes')
  const clientUuids = []
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]
    clientUuids.push(row.clientUuid)
    await appendOneCrash(crashesFolder, zip, row, index, formModel)
  }
  return clientUuids
}

/**
 * @param {import('jszip')} crashesFolder
 * @param {import('jszip')} zip
 * @param {{ clientUuid: string, payload: object }} row
 * @param {number} index
 * @param {Record<string, unknown> | null} formModel
 */
async function appendOneCrash(crashesFolder, zip, row, index, formModel) {
  const name = sanitizeCrashEntryFilename(index, row.clientUuid)
  const entryStem = getCrashEntryStem(name)
  const { jsonPayload, attachmentFiles } = await prepareCrashPayloadForZipExport(
    row.payload,
    formModel,
    entryStem,
  )
  crashesFolder.file(name, JSON.stringify(jsonPayload, null, 2))
  attachmentFiles.forEach(({ zipPath, bytes }) => {
    zip.file(zipPath, bytes)
  })
}
